/**
 * tourSeen — persistence for "has this user seen the product tour?".
 *
 * The server is the single source of truth: `users.metadata.tourSeenAt`, which
 * arrives on the user object via /auth/me (and the login response). This makes
 * "seen" follow the ACCOUNT across devices, and — importantly — makes the state
 * predictable: clearing the DB flag reliably re-enables the tour.
 *
 * We deliberately do NOT keep a localStorage copy. An independent device-local
 * flag could (and did) suppress the tour even after the DB flag was cleared,
 * and we always have the DB value once the user is authenticated.
 */
import { api } from "@/lib/api-client";
import type { User } from "@/types/api";

/** Has this user already seen / completed / skipped the tour? */
export function hasTourBeenSeen(user: User | null | undefined): boolean {
  return !!user?.metadata?.tourSeenAt;
}

/** Record that the user has seen the tour (completed or skipped — both count).
 *  Persists to the account; best-effort (a failed call just means it'll be
 *  marked on the next successful run). */
export function markTourSeen(_user?: User | null): void {
  void api.post("/api/auth/me/tour-seen").catch(() => { /* best-effort */ });
}
